<template>
  <parent>
    <loading-page v-if="loading" />
    <div
      v-else
      class="cache-policies-editor h-100"
    >
      <validation-observer
        ref="rules"
        v-slot="{ invalid }"
      >
        <form-wrapper full-width>
          <template v-slot:body>
            <paper>
              <template v-slot:title>
                <p class="mb-0">
                  Info
                </p>
              </template>
              <div class="px-2 col-12 col-lg-6 m-auto">
                <validation-provider
                  #default="{ errors }"
                  name="Name"
                  rules="required"
                  class="w-100 mb-1"
                >
                  <input-field
                    name="name"
                    label="Name"
                    label-position="top"
                    class="no-icon"
                    :error="errors.length ? errors[0] : null"
                  >
                    <b-form-input
                      id="name"
                      v-model="requestPolicy.name"
                      placeholder="Add name here..."
                      :state="errors.length > 0 ? false : null"
                    />
                  </input-field>
                </validation-provider>

                <input-field
                  name="comment"
                  label="Comment"
                  label-position="top"
                  class="no-icon"
                >
                  <b-form-input
                    id="comment"
                    v-model="requestPolicy.comment"
                    placeholder="Add comment here..."
                  />
                </input-field>
              </div>
            </paper>

            <paper>
              <template v-slot:title>
                <div>
                  <p>Request key contents</p>
                  <span
                    class="text-regular"
                  >The request key includes the headers, cookies, and query
                    strings in the cache policy, as follows:</span>
                </div>
              </template>
              <div class="px-2 col-12 col-lg-6 m-auto">
                <div class="d-flex full-form">
                  <div class="w-100">
                    <label for="headerOptions">Headers</label>
                    <span
                      class="subtitle bold"
                    >Choose headers to add to the request key</span>
                    <custom-select
                      v-if="requestPolicyModel.headerOptions"
                      id="headerOptions"
                      v-model="requestPolicy.headerPolicyId"
                      :options="getOptions(requestPolicyModel.headerOptions)"
                    />
                    <template v-if="requestPolicy.headerPolicyId === 1">
                      <div class="d-flex add-new">
                        <b-form-input
                          id="headerPolicyValue"
                          v-model="headerPolicyValue"
                          :class="{ error: invalidString }"
                          @click="invalidString = false"
                          @keyup.enter="addHeader"
                        />
                        <b-button
                          variant="bg-white"
                          @click="addHeader"
                        >
                          Add Header
                        </b-button>
                      </div>
                      <div
                        v-if="headers[requestPolicy.headerPolicyId].length > 0"
                      >
                        <template
                          v-for="(header, index) in headers[
                            requestPolicy.headerPolicyId
                          ]"
                        >
                          <b-button
                            :key="header"
                            class="margin-5"
                            variant="bg-white"
                          >
                            {{ header }}
                            <a @click="removeHeadersArrayItem(index)">
                              <GjIcon
                                class="icon-right"
                                name="Close"
                              />
                            </a>
                          </b-button>
                        </template>
                      </div>
                    </template>
                  </div>
                  <a
                    @click="
                      showInfo(messages.headers.title, messages.headers.message)
                    "
                  >
                    <GjIcon
                      class="icon-right form__icon"
                      name="Info"
                    />
                  </a>
                </div>

                <div class="d-flex full-form">
                  <div class="w-100">
                    <label for="headerOptions">Cookies</label>
                    <span
                      class="subtitle bold"
                    >Choose cookies to add to the cache key</span>
                    <custom-select
                      v-if="requestPolicyModel.cookieOptions"
                      id="headerOptions"
                      v-model="requestPolicy.cookiePolicyId"
                      :options="getOptions(requestPolicyModel.cookieOptions)"
                    />
                    <template
                      v-if="
                        requestPolicy.cookiePolicyId !== 0 &&
                          requestPolicy.cookiePolicyId !== 1
                      "
                    >
                      <div class="d-flex add-new">
                        <b-form-input
                          id="cookiePolicyValue"
                          v-model="cookiePolicyValue"
                          :class="{ error: invalidCookieString }"
                          @click="invalidCookieString = false"
                          @keyup.enter="addCookie"
                        />
                        <b-button
                          variant="bg-white"
                          @click="addCookie"
                        >
                          Add Cookie
                        </b-button>
                      </div>
                      <div
                        v-if="
                          cookies[requestPolicy.cookiePolicyId].length > 0 &&
                            requestPolicy.cookiePolicyId !== 1
                        "
                      >
                        <template
                          v-for="(cookie, index) in cookies[
                            requestPolicy.cookiePolicyId
                          ]"
                        >
                          <b-button
                            :key="cookie"
                            class="margin-5"
                            variant="bg-white"
                          >
                            {{ cookie }}
                            <a @click="removeCookiesArrayItem(index)">
                              <GjIcon
                                class="icon-right"
                                name="Close"
                              />
                            </a>
                          </b-button>
                        </template>
                      </div>
                    </template>
                  </div>
                  <a
                    @click="
                      showInfo(messages.cookies.title, messages.cookies.message)
                    "
                  >
                    <GjIcon
                      class="icon-right form__icon"
                      name="Info"
                    />
                  </a>
                </div>

                <div class="d-flex full-form">
                  <div class="w-100">
                    <label for="queryStringOptions">Query strings</label>
                    <span
                      class="subtitle bold"
                    >Choose query strings to add to the cache key</span>
                    <custom-select
                      v-if="requestPolicyModel.queryStringOptions"
                      id="queryStringOptions"
                      v-model="requestPolicy.queryStringPolicyId"
                      :options="
                        getOptions(requestPolicyModel.queryStringOptions)
                      "
                    />
                    <template
                      v-if="
                        requestPolicy.queryStringPolicyId !== 0 &&
                          requestPolicy.queryStringPolicyId !== 1
                      "
                    >
                      <div class="d-flex add-new">
                        <b-form-input
                          id="queryStringPolicyValue"
                          v-model="queryStringPolicyValue"
                          :class="{ error: invalidQueryString }"
                          @click="invalidQuweyString = false"
                          @keyup.enter="addQueryString"
                        />
                        <b-button
                          variant="bg-white"
                          @click="addQueryString"
                        >
                          Add Query String
                        </b-button>
                      </div>
                      <div
                        v-if="
                          queryStrings[requestPolicy.queryStringPolicyId]
                            .length > 0 &&
                            requestPolicy.queryStringPolicyId !== 1
                        "
                      >
                        <template
                          v-for="(queryString, index) in queryStrings[
                            requestPolicy.queryStringPolicyId
                          ]"
                        >
                          <b-button
                            :key="queryString"
                            variant="bg-white"
                            class="margin-5"
                          >
                            {{ queryString }}
                            <a @click="removeQueryStringsArrayItem(index)">
                              <GjIcon
                                class="icon-right"
                                name="Close"
                              />
                            </a>
                          </b-button>
                        </template>
                      </div>
                    </template>
                  </div>
                  <a
                    @click="
                      showInfo(
                        messages.queryStrings.title,
                        messages.queryStrings.message,
                      )
                    "
                  >
                    <GjIcon
                      class="icon-right form__icon"
                      name="Info"
                    />
                  </a>
                </div>
              </div>
            </paper>
          </template>
          <template v-slot:footer>
            <b-button
              variant="bg-white"
              @click="$emit('close')"
            >
              Cancel
            </b-button>
            <button-loading
              v-if="details"
              variant="bg-primary"
              :disabled="invalid || tagsOk"
              :spinning="updatingRequestPolicy"
              class="ml-16"
              @click="onEdit"
            >
              Update
            </button-loading>
            <button-loading
              v-if="!details"
              variant="bg-primary"
              :disabled="invalid || tagsOk"
              :spinning="addingRequestPolicy"
              class="ml-16"
              @click="onCreate"
            >
              Create
            </button-loading>
          </template>
        </form-wrapper>
      </validation-observer>

      <info
        v-if="openInfo"
        :title="infoTitle"
        :message="infoMessage"
        @close="openInfo = false"
      />
    </div>
  </parent>
</template>
<script>
import InputField from '@/components/form/InputField.vue'
import Paper from '@/components/paper/Paper.vue'

import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import messages from '../../messages'

import redirectError from '@/services/redirectError'

export default {
  name: 'RequestPoliciesFrom',
  components: {
    ValidationProvider,
    ValidationObserver,
    InputField,
    Paper,
  },
  props: {
    row: {
      default: null,
      type: Object,
    },
    details: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      messages,
      required,
      openInfo: false,
      infoTitle: '',
      infoMessage: '',
      selected: null,
      requestPolicyModel: {},
      headerPolicyValue: '',
      headers: [[], [], [], []],
      invalidString: false,
      cookiePolicyValue: '',
      cookies: [[], [], []],
      invalidCookieString: false,
      queryStringPolicyValue: '',
      queryStrings: [[], [], []],
      invalidQueryString: false,
    }
  },
  computed: {
    requestPolicy() {
      return this.$store.state.requestPolicies.requestPolicy
    },
    loading() {
      return this.$store.state.requestPolicies.gettingRequestPolicy
    },
    updatingRequestPolicy() {
      return this.$store.state.requestPolicies.updatingRequestPolicy
    },
    addingRequestPolicy() {
      return this.$store.state.requestPolicies.addingRequestPolicy
    },
    queryStringsOk() {
      if (
        this.requestPolicy.queryStringPolicyId !== 0
        && this.requestPolicy.queryStringPolicyId !== 1
      ) {
        return (
          this.queryStrings[this.requestPolicy.queryStringPolicyId].length === 0
        )
      }
      return false
    },
    headersOk() {
      if (this.requestPolicy.headerPolicyId === 1) {
        return this.headers[1].length === 0
      }
      return false
    },
    cookiesOk() {
      if (
        this.requestPolicy.cookiePolicyId !== 1
        && this.requestPolicy.cookiePolicyId !== 0
      ) {
        return this.cookies[this.requestPolicy.cookiePolicyId].length === 0
      }
      return false
    },
    tagsOk() {
      return this.headersOk || this.cookiesOk || this.queryStringsOk
    },
  },
  created() {
    if (this.details) {
      this.requestPolicyModel = this.requestPolicy
      this.cookies[
        this.requestPolicy.cookiePolicyId
      ] = this.requestPolicy.cookies
      this.headers[
        this.requestPolicy.headerPolicyId
      ] = this.requestPolicy.headers
      this.queryStrings[
        this.requestPolicy.queryStringPolicyId
      ] = this.requestPolicy.queryStrings
    } else {
      this.$store
        .dispatch('requestPolicies/getRequestPolicy', 0)
        .then(res => {
          this.requestPolicyModel = res.data.result
        })
        .catch(err => redirectError(err, this.$router, this.$route.path))
    }
  },
  methods: {
    onCreate() {
      this.validation()
        .then(() => {
          const localRequestPolicy = {
            id: this.requestPolicy.id,
            name: this.requestPolicy.name,
            comment: this.requestPolicy.comment,
            headerPolicyId: this.requestPolicy.headerPolicyId,
            headers: this.headers[this.requestPolicy.headerPolicyId],
            cookiePolicyId: this.requestPolicy.cookiePolicyId,
            cookies: this.cookies[this.requestPolicy.cookiePolicyId],
            queryStringPolicyId: this.requestPolicy.queryStringPolicyId,
            queryStrings: this.queryStrings[
              this.requestPolicy.queryStringPolicyId
            ],
          }
          this.$store
            .dispatch('requestPolicies/addRequestPolicy', localRequestPolicy)
            .then(() => {
              this.successToast('Reuqest policy was added')
              this.$store.dispatch('requestPolicies/fetchRequestPolicies')
              this.$emit('close')
              this.$emit('onCreate')
            })
            .catch(error => {
              this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while creating policy.')
            })
        })
        .catch(() => {})
    },
    onEdit() {
      const localRequestPolicy = {
        id: this.requestPolicy.id,
        name: this.requestPolicy.name,
        comment: this.requestPolicy.comment,
        headerPolicyId: this.requestPolicy.headerPolicyId,
        headers: this.headers[this.requestPolicy.headerPolicyId],
        cookiePolicyId: this.requestPolicy.cookiePolicyId,
        cookies: this.cookies[this.requestPolicy.cookiePolicyId],
        queryStringPolicyId: this.requestPolicy.queryStringPolicyId,
        queryStrings: this.queryStrings[this.requestPolicy.queryStringPolicyId],
      }

      this.validation()
        .then(() => {
          this.$emit('edit', localRequestPolicy)
        })
        .catch(() => {})
    },
    validation() {
      return new Promise((resolve, reject) => {
        this.$refs.rules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    addHeader() {
      const re = /^[a-zA-Z_-]+$/
      const term = this.headerPolicyValue
      if (re.test(term)) {
        this.headers[this.requestPolicy.headerPolicyId].push(term)
        this.headerPolicyValue = ''
      } else {
        this.invalidString = true
      }
    },
    removeHeadersArrayItem(index) {
      this.headers[this.requestPolicy.headerPolicyId].splice(index, 1)
    },
    addCookie() {
      const re = /^[a-zA-Z_-]+$/
      const term = this.cookiePolicyValue
      if (re.test(term)) {
        this.cookies[this.requestPolicy.cookiePolicyId].push(term)
        this.cookiePolicyValue = ''
      } else {
        this.invalidCookieString = true
      }
    },
    removeCookiesArrayItem(index) {
      this.cookies[this.requestPolicy.cookiePolicyId].splice(index, 1)
    },
    addQueryString() {
      const re = /^[a-zA-Z_-]+$/
      const term = this.queryStringPolicyValue
      if (re.test(term)) {
        this.queryStrings[this.requestPolicy.queryStringPolicyId].push(term)
        this.queryStringPolicyValue = ''
      } else {
        this.invalidQueryString = true
      }
    },
    removeQueryStringsArrayItem(index) {
      this.queryStrings[this.requestPolicy.queryStringPolicyId].splice(index, 1)
    },
    showInfo(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
  },
}
</script>
<style lang="scss" scoped>
.no-icon {
  margin-right: 32px;
  margin-bottom: 16px;
}
.subtitle {
  display: block;
  font-size: 12px;
  color: #667c99;
  font-weight: 500;
  margin-bottom: 6px;
}
.add-new {
  margin-top: 14px;
  .btn {
    min-width: 120px;
  }
}
.form__icon {
  margin-top: 55px;
}

.information {
  label {
    font-weight: 400;
  }
  .info {
    font-size: 16px;
  }
}
.margin-5 {
  margin: 0 5px 5px 0;
}
</style>
